/**
 * Tenant Filters Component
 *
 * Search and filter controls for tenant list.
 *
 * @fileoverview Tenant filtering component
 */

import React, { useState, useCallback } from 'react';
import {
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdSearch, MdClear } from 'react-icons/md';

interface TenantFiltersProps {
  search: string;
  isActive: boolean | undefined;
  onSearchChange: (search: string) => void;
  onIsActiveChange: (isActive: boolean | undefined) => void;
  onReset: () => void;
}

/**
 * Tenant Filters Component
 *
 * Provides search input and status filter for tenant list.
 */
export const TenantFilters: React.FC<TenantFiltersProps> = ({
  search,
  isActive,
  onSearchChange,
  onIsActiveChange,
  onReset,
}) => {
  const [searchValue, setSearchValue] = useState(search);
  const bgColor = useColorModeValue('white', 'gray.800');

  // Debounced search
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);

      // Debounce the actual search
      const timeoutId = setTimeout(() => {
        onSearchChange(value);
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [onSearchChange]
  );

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onIsActiveChange(undefined);
    } else {
      onIsActiveChange(value === 'true');
    }
  };

  const handleReset = () => {
    setSearchValue('');
    onReset();
  };

  const hasFilters = search || isActive !== undefined;

  return (
    <Flex
      gap={3}
      px={3}
      py={2}
      bg={bgColor}
      borderRadius="lg"
      flexWrap="wrap"
      align="center"
    >
      {/* Search input */}
      <InputGroup maxW="260px" size="sm">
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color="gray.400" boxSize={4} />
        </InputLeftElement>
        <Input
          placeholder="Search tenants..."
          value={searchValue}
          onChange={handleSearchChange}
        />
      </InputGroup>

      {/* Status filter */}
      <Select
        maxW="140px"
        size="sm"
        value={isActive === undefined ? '' : String(isActive)}
        onChange={handleStatusChange}
      >
        <option value="">All Status</option>
        <option value="true">Active</option>
        <option value="false">Inactive</option>
      </Select>

      {/* Reset button */}
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Icon as={MdClear} />}
          onClick={handleReset}
        >
          Clear Filters
        </Button>
      )}
    </Flex>
  );
};

export default TenantFilters;
